import { groupBy } from 'lodash';
import {
  mapLocationFilter,
  mappedFormatFilterName,
  mapPoiFilter,
  mapTagFilter,
  transformCustomPoiFilter,
} from 'components/pages/Planner/hooks/utils/transformFilters';
import { ProductTemplateRulesResponse } from 'components/common/types/DirectSalesProductTemplate.types';
import {
  DirectSalesLineObjective,
  DirectSalesLogicFilterTypes,
  DirectSalesObjectiveTarget,
  DirectSalesPOIFilter,
  DirectSalesTargetCriteria,
} from 'components/common/types/DirectSalesCampaign.types';
import { derivePoiDisplayFromProductTemplateCategoryPath } from './derivePoiDisplayFromProductTemplateCategoryPath';
import { getHasChildrenFromProductTemplatePoiName } from './transformPoiFromResponse';
import { FlattenedFilter, GroupedFilters } from './transformObjectivesFromResponse.types';

const typeToCategory: Record<string, string> = {};
const typeToGroup: Record<string, 'formats' | 'locations' | 'tags' | 'poi'> = {};

const fillTypeMaps = (map: Record<string, string>, groupName: 'formats' | 'locations' | 'tags' | 'poi'): void => {
  Object.entries(map).forEach(([category, type]) => {
    typeToCategory[type] = category;
    typeToGroup[type] = groupName;
  });
};

fillTypeMaps(mappedFormatFilterName, 'formats');
fillTypeMaps(mapLocationFilter, 'locations');
fillTypeMaps(mapTagFilter, 'tags');

Object.keys(mapPoiFilter).forEach((type) => {
  typeToCategory[type] = type;
  typeToGroup[type] = 'poi';
});

const flattenFilters = (
  filters: {
    type: string;
    filters?: DirectSalesLogicFilterTypes[];
    filter?: { filters: DirectSalesLogicFilterTypes[] };
  }[] = [],
): FlattenedFilter[] => {
  if (!filters.length) return [];

  return filters.flatMap<FlattenedFilter>((filter) => {
    const { type } = filter;

    if (type === 'OrFilter') {
      return (filter.filters ?? []).map((leaf) => ({ ...leaf, include: true }));
    }
    if (type === 'NotFilter') {
      return (filter.filter?.filters ?? []).map((leaf) => ({ ...leaf, include: false }));
    }
    return [];
  });
};

const groupFilters = (filters: FlattenedFilter[]): GroupedFilters => {
  return filters.reduce<GroupedFilters>(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (grouped, filter) => {
      const { type } = filter;

      if (type === 'UnitListFilter' && 'unitsIds' in filter) {
        filter.unitsIds.forEach((unitId) => {
          grouped.frameList.routeFrameCodes.push({
            code: unitId,
            name: unitId,
            include: filter.include,
          });
        });
        return grouped;
      }

      if (type === 'CustomPoiFilter' && 'values' in filter) {
        grouped.poi.push(...transformCustomPoiFilter(filter, filter.include));
        return grouped;
      }

      const group = typeToGroup[type];

      if (!('id' in filter) || !group) {
        return grouped;
      }

      if (type === 'TagFilter') {
        grouped.tags.push({
          code: filter.id,
          name: filter.name ?? '',
          include: filter.include,
          category: filter.name ?? '',
        });
        return grouped;
      }

      if (group !== 'poi') {
        grouped[group].push({
          category: typeToCategory[type],
          code: filter.id,
          name: filter.name ?? '',
          include: filter.include,
        });
        return grouped;
      }

      const poiFilter = filter as DirectSalesPOIFilter & { include: boolean };
      const categoryPath = poiFilter.id;
      const poiCategoryPathDisplay = derivePoiDisplayFromProductTemplateCategoryPath(categoryPath);
      const rawPoiName = poiFilter.name;
      const name = rawPoiName || poiCategoryPathDisplay.leafName;

      grouped.poi.push({
        code: categoryPath,
        name,
        include: poiFilter.include,
        radius: poiFilter.radius ?? 0,
        dataProvider: poiFilter.dataProvider ?? '',
        hasChildren: getHasChildrenFromProductTemplatePoiName(rawPoiName),
        rootCategoryType: poiCategoryPathDisplay.rootCategoryType,
        parentCategory: poiCategoryPathDisplay.parentCategory,
        categoryPath,
        tooltip: poiCategoryPathDisplay.tooltip,
      });

      return grouped;
    },
    {
      formats: [],
      locations: [],
      tags: [],
      poi: [],
      frameList: { listFiles: [], routeFrameCodes: [] },
    },
  );
};

const buildLineObjective = (group: DirectSalesTargetCriteria[]): DirectSalesLineObjective => {
  const first = group[0];
  const leaves = flattenFilters(first.filter?.filters) ?? [];
  const { formats, locations, poi, tags, frameList } = groupFilters(leaves);

  const targets = group.reduce((acc: Record<string, DirectSalesTargetCriteria>, target) => {
    acc[target.criteriaType] = target;
    return acc;
  }, {});

  const formatTarget = (target?: DirectSalesTargetCriteria): DirectSalesObjectiveTarget => {
    const isValue = target?.value;

    return {
      value: isValue ? target.value.target : 0,
      priority: {
        code: isValue ? String(target.value.priority) : '',
        name: isValue ? String(target.value.priority) : '',
      },
      greenTolerance: isValue ? target.value.greenTolerance * 100 : 0,
      orangeTolerance: isValue ? target.value.orangeTolerance * 100 : 0,
      objectiveId: null,
    };
  };

  return {
    objectiveGroupId: first.objectiveGroupId ?? 0,
    name: first.name ?? '',
    formats,
    locations,
    poi,
    tags,
    frameList,
    budget: formatTarget(targets.BUDGET_TARGET),
    frames: formatTarget(targets.FRAME_TARGET),
    impressions: formatTarget(targets.VOLUME_TARGET),
  };
};

export const transformObjectives = (foundObjectives: ProductTemplateRulesResponse): DirectSalesLineObjective[] => {
  const fixedSet = foundObjectives?.fixedSet ?? [];
  const defaultSet = foundObjectives?.defaultSet ?? [];

  if (fixedSet.length) return Object.values(groupBy(fixedSet, 'objectiveGroupId')).map(buildLineObjective);
  if (defaultSet.length) return Object.values(groupBy(defaultSet, 'objectiveGroupId')).map(buildLineObjective);
  return [];
};
